import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  inject,
  input,
  model,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { CdkConnectedOverlay, ConnectedPosition } from '@angular/cdk/overlay';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  EMPTY,
  Observable,
  Subject,
  catchError,
  debounce,
  debounceTime,
  forkJoin,
  map,
  of,
  startWith,
  switchMap,
  timer,
} from 'rxjs';
import { TcdlObjectRef, TcdlToken, objectReferences, tokenizeLine } from '../../core/tcdl/tcdl-tokenizer';
import {
  LibraryState,
  TcdlFinding,
  TcdlFindingKind,
  collectLibraries,
  findingsFor,
} from '../../core/tcdl/tcdl-object-check';
import {
  TcdlCompletion,
  commandSuggestions,
  completionContext,
  fold,
  functionInsertText,
  functionSuggestions,
} from '../../core/tcdl/tcdl-completion';
import { ObjectMappingService } from '../../services/object-mapping.service';
import { SecretEncryptDialogComponent } from '../secret-encrypt-dialog/secret-encrypt-dialog.component';

export type TcdlEditorSize = 'default' | 'compact';

interface TcdlLine {
  readonly text: string;
  readonly trimmed: string;
  readonly index: number;
  readonly isEmpty: boolean;
  readonly isManual: boolean;
  readonly tokens: readonly TcdlToken[];
  readonly objects: readonly TcdlObjectRef[];
}

interface TcdlLineParts {
  readonly tokens: readonly TcdlToken[];
  readonly objects: readonly TcdlObjectRef[];
}

/** A colored part of a line; an object split for a marker keeps the object kind on every piece. */
export interface TcdlPart extends TcdlToken {
  readonly unknownObject?: boolean;
  readonly unknownFunction?: boolean;
}

/** Entry names per checked library; `null` when the project has no library of that name. */
type ObjectLookup = ReadonlyMap<string, ReadonlySet<string> | null>;

/** Where a picked suggestion goes, as offsets into the whole value. */
interface SuggestionTarget {
  readonly from: number;
  readonly to: number;
}

interface ObjectRequest {
  readonly projectId: string;
  readonly completion: TcdlCompletion;
  readonly target: SuggestionTarget;
}

const SUGGESTION_DEBOUNCE_MS = 150;
const OBJECT_CHECK_DEBOUNCE_MS = 300;

const DIAGNOSTIC_KEYS: Record<TcdlFindingKind, string> = {
  'unknown-command': 'tcdlEditor.diagnostic.unknownCommand',
  'not-implemented': 'tcdlEditor.diagnostic.notImplemented',
  'unknown-library': 'tcdlEditor.diagnostic.unknownLibrary',
  'unknown-entry': 'tcdlEditor.diagnostic.unknownEntry',
  'unknown-function': 'tcdlEditor.diagnostic.unknownFunction',
};

function isObjectFinding(finding: TcdlFinding): boolean {
  return finding.kind === 'unknown-library' || finding.kind === 'unknown-entry';
}

/** Cuts the tokens at the marked object and function ranges; joined again they still give the line unchanged. */
function splitAtFindings(tokens: readonly TcdlToken[], findings: readonly TcdlFinding[]): readonly TcdlPart[] {
  const marked = findings.filter((f) => isObjectFinding(f) || f.kind === 'unknown-function');
  if (!marked.length) return tokens;
  const parts: TcdlPart[] = [];
  let at = 0;
  for (const token of tokens) {
    const end = at + token.text.length;
    let cut = at;
    for (const f of marked) {
      const from = Math.max(f.start, at);
      const to = Math.min(f.start + f.length, end);
      if (from >= to) continue;
      if (from > cut) parts.push({ ...token, text: token.text.slice(cut - at, from - at) });
      const mark = f.kind === 'unknown-function' ? { unknownFunction: true } : { unknownObject: true };
      parts.push({ ...token, text: token.text.slice(from - at, to - at), ...mark });
      cut = to;
    }
    if (cut < end) parts.push(cut === at ? token : { ...token, text: token.text.slice(cut - at) });
    at = end;
  }
  return parts;
}

function sameList(a: TcdlCompletion, b: TcdlCompletion): boolean {
  return a.source === b.source && (a.library ?? '').toLowerCase() === (b.library ?? '').toLowerCase();
}

let uniqueHintId = 0;

/**
 * Klassik-TCDL-Skripteditor mit Line-Gutter, Manuell-Marker („M"), Caret-
 * Highlight und Statistikzeile. Nutzt eine einzelne `<textarea>` als Quelle
 * der Wahrheit; Gutter, Overlay und Statistik werden aus dem Wert abgeleitet
 * — kein DOM-Sync, kein IE-Trick.
 *
 * `size='compact'` erwartet in der Testschritt-Tabelle (schmaleres Gutter,
 * kleinere Schriftgröße), `size='default'` im Ablauf-Editor der
 * Testschritt-Neu/Bearbeiten-Seite.
 *
 * Bindung erfolgt bewusst über `[value]` / `(valueChange)` (Angular `model()`),
 * nicht über `ControlValueAccessor`: die konsumierenden Formulare halten TCDL
 * unterschiedlich — mal als eigenständiges Signal, mal als Control einer
 * Reactive Form — binden es aber beide über ein Signal ein, wofür ein CVA
 * nichts beiträgt.
 */
@Component({
  selector: 'app-tcdl-editor',
  standalone: true,
  imports: [TranslatePipe, CdkConnectedOverlay, SecretEncryptDialogComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './tcdl-editor.component.html',
  styleUrl: './tcdl-editor.component.scss',
  host: { '[class.tcdl-editor-host--compact]': "size() === 'compact'" },
})
export class TcdlEditorComponent {
  private readonly translate = inject(TranslateService);
  private readonly objectMappings = inject(ObjectMappingService);

  readonly value = model<string>('');
  readonly freetext = input<string>('');
  readonly size = input<TcdlEditorSize>('default');
  readonly ariaLabel = input<string | undefined>(undefined);
  readonly placeholder = input<string | undefined>(undefined);
  readonly maxLength = input<number | null>(null);
  readonly disabled = input<boolean>(false);
  readonly projectId = input<string | null>(null);

  readonly advanceRequested = output<void>();

  readonly textarea = viewChild.required<ElementRef<HTMLTextAreaElement>>('ta');

  readonly hintId = `tcdl-editor-hint-${++uniqueHintId}`;
  readonly listboxId = `tcdl-editor-suggestions-${uniqueHintId}`;
  readonly diagnosticId = `tcdl-editor-diagnostic-${uniqueHintId}`;

  readonly focused = signal(false);
  readonly caretLine = signal(0);
  readonly liveAnnouncement = signal('');
  readonly overlayOffset = signal('translate(0, 0)');
  readonly caretColumnText = signal('');

  readonly suggestions = signal<readonly string[]>([]);
  readonly suggestionsLoading = signal(false);
  readonly activeSuggestion = signal(0);
  readonly suggestionsOpen = computed(() => this.suggestionsLoading() || this.suggestions().length > 0);
  readonly activeDescendant = computed(() =>
    this.suggestions().length ? this.optionId(this.activeSuggestion()) : null,
  );
  readonly suggestionPositions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
  ];

  readonly encryptDialogOpen = signal(false);
  readonly canEncrypt = computed(() => !!this.projectId() && !this.disabled());
  /** Where the encrypted value goes; the textarea loses its focus while the dialog is open. */
  private encryptTarget: SuggestionTarget | null = null;

  private suggestionTarget: SuggestionTarget | null = null;
  /** What the shown list completes; a new keystroke keeps the list only for the same kind. */
  private listedFor: TcdlCompletion | null = null;
  private picking = false;
  /** The text the list was made for; a change from outside makes its target stale. */
  private listedValue = '';
  private pendingRequest: ObjectRequest | null = null;
  private readonly objectRequests = new Subject<ObjectRequest | null>();

  readonly resolvedAriaLabel = computed(
    () => this.ariaLabel() ?? this.translate.instant('testSteps.form.tcdlEditorAriaLabel'),
  );

  readonly resolvedPlaceholder = computed(
    () => this.placeholder() ?? this.translate.instant('testCases.form.testSteps.tcdlPlaceholder'),
  );

  // Per instance and per line text: the step table embeds the editor once per row, and a
  // keystroke changes one line. Rebuilt from the current lines, so it never outgrows them.
  private tokenCache = new Map<string, TcdlLineParts>();

  readonly lines = computed<TcdlLine[]>(() => {
    const raw = this.value();
    const parts = raw.split('\n');
    const previous = this.tokenCache;
    const cache = new Map<string, TcdlLineParts>();
    const lines = parts.map((text, index) => {
      const trimmed = text.trim();
      const isEmpty = trimmed.length === 0;
      const known = cache.get(text) ?? previous.get(text);
      const tokens = known?.tokens ?? tokenizeLine(text);
      const line = known ?? { tokens, objects: objectReferences(text, tokens) };
      cache.set(text, line);
      return {
        text,
        trimmed,
        index,
        isEmpty,
        isManual: isEmpty && index < parts.length - 1,
        ...line,
      };
    });
    this.tokenCache = cache;
    return lines;
  });

  /** Answers of the last object check; a library not in it is still loading. */
  readonly objectLookup = signal<ObjectLookup>(new Map());
  readonly objectCheckFailed = signal(false);

  readonly findings = computed<TcdlFinding[][]>(() => {
    const lookup = this.objectLookup();
    const failed = this.objectCheckFailed();
    const stateOf = (library: string): LibraryState =>
      failed || !lookup.has(library) ? 'loading' : (lookup.get(library) ?? 'missing');
    return this.lines().map((line) => findingsFor(line.text, stateOf, line.tokens, line.objects));
  });

  readonly renderedLines = computed(() =>
    this.lines().map((line, i) => ({ ...line, parts: splitAtFindings(line.tokens, this.findings()[i]) })),
  );

  readonly unknownObjectCount = computed(() => this.findings().flat().filter(isObjectFinding).length);

  readonly unknownObjectLines = computed(() => this.findings().map((line) => line.some(isObjectFinding)));

  /** Explains the first marker of the caret line; empty without focus or marker. */
  readonly diagnostic = computed(() => {
    const line = this.caretLine();
    const finding = this.focused() ? this.findings()[line]?.[0] : undefined;
    if (!finding) return '';
    const text = this.translate.instant(DIAGNOSTIC_KEYS[finding.kind], finding);
    return this.translate.instant('tcdlEditor.diagnostic.line', { line: line + 1, text });
  });

  readonly instructionCount = computed(() => this.lines().filter((l) => !l.isEmpty).length);
  readonly manualCount = computed(() => this.lines().filter((l) => l.isManual).length);
  readonly unknownCount = computed(() => this.countCommands('unknown'));
  readonly notImplementedCount = computed(() => this.countCommands('not-implemented'));
  readonly hasFreetext = computed(() => this.freetext().trim().length > 0);

  constructor() {
    toObservable(computed(() => ({ projectId: this.projectId(), value: this.value() })))
      .pipe(
        // A fresh timer per change: debounceTime reuses its first timer, which may run outside the test zone.
        debounce(() => timer(OBJECT_CHECK_DEBOUNCE_MS)),
        switchMap(({ projectId, value }) => this.checkObjects(projectId, value)),
        takeUntilDestroyed(),
      )
      .subscribe((lookup) => {
        this.objectCheckFailed.set(lookup === null);
        if (lookup) this.objectLookup.set(lookup);
      });

    this.objectRequests
      .pipe(
        debounceTime(SUGGESTION_DEBOUNCE_MS),
        switchMap((request) => (request ? this.loadObjects(request) : EMPTY)),
        takeUntilDestroyed(),
      )
      .subscribe(({ request, names, loading }) => {
        // A late answer must not replace what the cursor offers by now.
        if (request !== this.pendingRequest) return;
        if (loading) {
          this.suggestionsLoading.set(true);
          return;
        }
        this.showSuggestions(names, request.target, request.completion);
      });
  }

  onInput(event: Event): void {
    if (this.disabled()) return;
    const target = event.target as HTMLTextAreaElement;
    this.value.set(target.value);
    this.syncCaret(target);
    if (!this.picking) this.updateSuggestions(target, false);
  }

  onCaretChange(event: Event): void {
    const ta = event.target as HTMLTextAreaElement;
    this.syncCaret(ta);
    // A click, a key or a selection elsewhere leaves the word the list was made for.
    if (!this.selectionInTarget(ta)) this.closeSuggestions();
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      this.closeSuggestions();
      this.advanceRequested.emit();
      return;
    }
    if (event.key === ' ' && event.ctrlKey) {
      event.preventDefault();
      if (!this.disabled()) this.updateSuggestions(event.target as HTMLTextAreaElement, true);
      return;
    }
    if (this.suggestionsOpen()) this.onSuggestionKey(event);
  }

  pickSuggestion(index: number): void {
    const name = this.suggestions()[index];
    const target = this.suggestionTarget;
    const isFunction = this.listedFor?.source === 'function';
    this.closeSuggestions();
    if (name === undefined || !target) return;
    this.replaceRange(target, isFunction ? functionInsertText(name) : name);
  }

  openEncryptDialog(): void {
    if (!this.canEncrypt()) return;
    const ta = this.textarea().nativeElement;
    this.encryptTarget = { from: ta.selectionStart ?? ta.value.length, to: ta.selectionEnd ?? ta.value.length };
    this.closeSuggestions();
    this.encryptDialogOpen.set(true);
  }

  onEncryptInserted(encrypted: string): void {
    const target = this.encryptTarget;
    this.encryptTarget = null;
    if (target && !this.disabled()) this.replaceRange(target, encrypted);
  }

  onEncryptDialogClosed(): void {
    this.encryptDialogOpen.set(false);
    const ta = this.textarea().nativeElement;
    ta.focus();
    if (this.encryptTarget) ta.setSelectionRange(this.encryptTarget.from, this.encryptTarget.to);
    this.encryptTarget = null;
  }

  optionId(index: number): string {
    return `${this.listboxId}-${index}`;
  }

  onScroll(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    this.overlayOffset.set(`translate(${-target.scrollLeft}px, ${-target.scrollTop}px)`);
  }

  onFocus(): void {
    this.focused.set(true);
    this.liveAnnouncement.set('');
  }

  onBlur(event: FocusEvent): void {
    this.focused.set(false);
    this.closeSuggestions();
    const target = event.target as HTMLTextAreaElement;
    const parts = target.value.split('\n');
    const line = parts[this.caretLine()] ?? '';
    if (!line.trim() && this.caretLine() < parts.length - 1) {
      this.liveAnnouncement.set(this.translate.instant('testSteps.form.tcdlManualAnnouncement'));
    }
  }

  applyFreetext(): void {
    if (this.disabled()) return;
    const freetext = this.freetext();
    if (!freetext.trim()) return;
    const converted = freetext
      .split(/\n+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0)
      .join('\n');
    const max = this.maxLength();
    this.value.set(max !== null && converted.length > max ? converted.slice(0, max) : converted);
  }

  focusTextarea(): void {
    this.textarea().nativeElement.focus();
  }

  isCurrentLine(index: number): boolean {
    return this.focused() && this.caretLine() === index;
  }

  private replaceRange(target: SuggestionTarget, insert: string): void {
    const ta = this.textarea().nativeElement;
    const text = ta.value.slice(0, target.from) + insert + ta.value.slice(target.to);
    const caret = target.from + insert.length;
    ta.focus();
    ta.setSelectionRange(target.from, target.to);
    this.picking = true;
    // insertText keeps the browser's undo history (Ctrl+Z); setting the value would erase it.
    const inserted = document.execCommand('insertText', false, insert) && ta.value === text;
    this.picking = false;
    if (!inserted) ta.value = text;
    ta.setSelectionRange(caret, caret);
    this.value.set(text);
    this.syncCaret(ta);
  }

  /** `null` when a request failed; the editor then hides every object marker. */
  private checkObjects(projectId: string | null, value: string): Observable<ObjectLookup | null> {
    const libraries = collectLibraries(value.split('\n'));
    if (!projectId || !libraries.length) return of(new Map());
    return forkJoin(
      libraries.map((library) =>
        this.objectMappings.libraryEntries(projectId, library).pipe(map((entries) => [library, entries] as const)),
      ),
    ).pipe(
      map((answers) => new Map(answers)),
      catchError(() => of(null)),
    );
  }

  private countCommands(status: TcdlToken['status']): number {
    return this.lines().filter((l) => l.tokens.some((t) => t.kind === 'command' && t.status === status)).length;
  }

  private syncCaret(target: HTMLTextAreaElement): void {
    const pos = target.selectionStart ?? 0;
    const before = target.value.slice(0, pos);
    const totalLines = target.value.split('\n').length;
    const line = before.split('\n').length - 1;
    this.caretLine.set(Math.min(line, Math.max(0, totalLines - 1)));
    this.caretColumnText.set(before.slice(before.lastIndexOf('\n') + 1));
  }

  /** True when nothing is listed, or the caret and value are still those the list was made for. */
  private selectionInTarget(ta: HTMLTextAreaElement): boolean {
    const target = this.suggestionTarget ?? this.pendingRequest?.target;
    if (!target) return true;
    // A selection (Ctrl+A, dragging) is not a cursor in the word: Enter must replace it as usual.
    const caret = ta.selectionStart ?? 0;
    const collapsed = caret === (ta.selectionEnd ?? caret);
    return collapsed && ta.value === this.listedValue && caret >= target.from && caret <= target.to;
  }

  private onSuggestionKey(event: KeyboardEvent): void {
    const count = this.suggestions().length;
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        this.closeSuggestions();
        return;
      case 'ArrowDown':
      case 'ArrowUp':
        if (!count) return;
        event.preventDefault();
        this.activeSuggestion.update((i) => (i + (event.key === 'ArrowDown' ? 1 : count - 1)) % count);
        return;
      case 'Enter':
      case 'Tab':
        if (!count) return;
        if (!this.selectionInTarget(this.textarea().nativeElement)) {
          this.closeSuggestions();
          return;
        }
        event.preventDefault();
        this.pickSuggestion(this.activeSuggestion());
        return;
      case 'ArrowLeft':
      case 'ArrowRight':
      case 'Home':
      case 'End':
      case 'PageUp':
      case 'PageDown':
        this.closeSuggestions();
    }
  }

  /** `byHand` (Ctrl+Space) also lists every command on a line that is still empty. */
  private updateSuggestions(ta: HTMLTextAreaElement, byHand: boolean): void {
    const caret = ta.selectionStart ?? ta.value.length;
    const lineStart = ta.value.lastIndexOf('\n', caret - 1) + 1;
    const lineEnd = ta.value.indexOf('\n', caret);
    const line = ta.value.slice(lineStart, lineEnd < 0 ? undefined : lineEnd);
    const completion = completionContext(line, caret - lineStart);
    this.listedValue = ta.value;
    const previous = this.listedFor;
    const shown = this.suggestions();
    this.closeSuggestions();
    if (!completion) return;
    const target = { from: lineStart + completion.replaceFrom, to: lineStart + completion.replaceTo };
    if (completion.source === 'command') {
      const names = completion.prefix.trim() || byHand ? commandSuggestions(completion.prefix) : [];
      this.showSuggestions(names, target, completion);
      return;
    }
    if (completion.source === 'function') {
      this.showSuggestions(functionSuggestions(completion.prefix), target, completion);
      return;
    }
    const projectId = this.projectId();
    if (!projectId) return;
    // Until the answer arrives, the previous list stays, narrowed to what is typed now.
    if (previous && sameList(previous, completion)) {
      const typed = completion.prefix.trim().toLowerCase();
      this.showSuggestions(
        shown.filter((name) => name.toLowerCase().startsWith(typed)),
        target,
        completion,
      );
    }
    this.pendingRequest = { projectId, completion, target };
    this.objectRequests.next(this.pendingRequest);
  }

  private loadObjects(request: ObjectRequest): Observable<{
    request: ObjectRequest;
    names: readonly string[];
    loading: boolean;
  }> {
    const { projectId, completion } = request;
    const names =
      completion.source === 'entry'
        ? this.objectMappings.entryNames(projectId, completion.library!, completion.prefix)
        : this.objectMappings.libraryNames(projectId, completion.prefix);
    return names.pipe(
      catchError(() => of([] as string[])),
      map((found) => ({ request, names: found, loading: false })),
      startWith({ request, names: [] as string[], loading: true }),
    );
  }

  private showSuggestions(
    names: readonly string[],
    target: SuggestionTarget | null,
    completion: TcdlCompletion | null,
  ): void {
    const offered = target && completion ? this.worthOffering(names, target, completion) : [];
    this.suggestionsLoading.set(false);
    this.suggestions.set(offered);
    this.activeSuggestion.set(0);
    this.suggestionTarget = offered.length ? target : null;
    this.listedFor = offered.length ? completion : null;
  }

  /** Drops what would break maxLength, and a list whose only entry is already typed out (DRUECKE = DRÜCKE). */
  private worthOffering(names: readonly string[], target: SuggestionTarget, completion: TcdlCompletion): string[] {
    const max = this.maxLength();
    const rest = this.textarea().nativeElement.value.length - (target.to - target.from);
    const fitting = names.filter((name) => max === null || rest + name.length <= max);
    return fitting.length === 1 && fold(fitting[0]) === fold(completion.prefix.trim()) ? [] : fitting;
  }

  private closeSuggestions(): void {
    this.pendingRequest = null;
    this.objectRequests.next(null);
    this.showSuggestions([], null, null);
  }
}
